import {useState} from 'react';
import {Box, Stack} from '@mui/material';
import moment, {Moment} from 'moment';
import {PlannerHeader} from './header/planner-header';
import {PlannerSiderPanel} from './side-panel/planner-sider-panel';
import {useHistory, useParams} from 'react-router-dom';
import {PlannerDisplayMode} from './shared/planner-display-mode';
import {useAllSearchParams} from '../../../../../../hooks/use-search-params';
import {PlannerBody} from './body/planner-body';
import {usePlannerFetchData} from './body/use-planner-fetch-data';
import {PlannerAddSelectionItem} from './header/planner-add-selection-item';
import {PlannerCreateActivityModal} from './modals/create/activity/_planner-create-activity-modal';
import {PlannerCreateAnniversaryModal} from './modals/create/anniversary/_planner-create-anniversary-modal';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {translations} from '../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {usePlannerItemMenu} from './body/planner-item-menu/use-planner-item-menu';
import {PlannerItemMenu} from './body/planner-item-menu/planner-item-menu';
import {PlannerConfirmItemDeleteModal} from './modals/delete/planner-confirm-item-delete-modal';
import {PlannerRegisterActivityModal} from './modals/register-activity/planner-register-activity-modal';
import {useFetchActivitiesSectorsAndGroups} from './shared/use-fetch-activities-sectors-and-groups';
import {Environment, LocalStorageKey} from '../../../../../../enums';
import {useAppContext} from '../../../../../../contexts/app/context';
import {useAuth} from '../../../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {
  calcEndDateByRange,
  calcStartDateByRange,
  DateRangeType,
} from '../../../../../../components/date-range-input/date-range-utils';

type PlannerURLParams = {
  date?: string;
  range?: DateRangeType;
  display?: PlannerDisplayMode;
};

export function CalendarProfessionalCaregiverOfficeScreen() {
  const history = useHistory();
  const {entityId} = useParams<{entityId: string}>();
  const searchParams = useAllSearchParams<PlannerURLParams>();
  const snackbar = useSnackBar();
  const {t} = useTranslation();
  const itemMenu = usePlannerItemMenu();
  const appContext = useAppContext();
  const {hasReadPermission} = useAuth();
  const remindersAvailable =
    appContext.state.configuration.environment === Environment.Development &&
    hasReadPermission(RolePermissionType.Seniors_PersonalData_Reminders);
  const [displayMode, setDisplayMode] = useState<PlannerDisplayMode>(() =>
    getInitialDisplayMode(searchParams)
  );
  const [rangeMode, setRangeMode] = useState<DateRangeType>(() =>
    getInitialRangeMode(searchParams)
  );
  const [startDate, setStartDate] = useState<Moment>(() =>
    calcStartDateByRange(rangeMode, moment(searchParams.date))
  );
  const [endDate, setEndDate] = useState<Moment>(() => calcEndDateByRange(rangeMode, startDate));
  const [addSelectedItem, setAddSelectedItem] = useState<PlannerAddSelectionItem | undefined>(
    undefined
  );

  // --------- Fetch data ---------
  const data = usePlannerFetchData({
    entityId,
    endDate: endDate,
    startDate: startDate,
    range: rangeMode,
  });
  // --------- Fetch activities sectors and groups ---------
  const activitiesSectorsAndGroups = useFetchActivitiesSectorsAndGroups();

  const updateSearchOptions = ({
    startDate,
    endDate,
    range,
    display,
  }: {
    endDate?: Moment;
    startDate?: Moment;
    range?: DateRangeType;
    display?: PlannerDisplayMode;
  }) => {
    // --- 0. Set new url params ---
    const params = new URLSearchParams(history.location.search);
    if (startDate) {
      params.set('date', startDate.format('YYYY-MM-DD'));
    }
    if (range) {
      params.set('range', range);
    }
    if (display) {
      params.set('display', display);
    }

    // --- 1. Update url ---
    history.push({search: params.toString()});

    // --- 2. Update state ---
    if (endDate) {
      setEndDate(endDate);
    }
    if (startDate) {
      setStartDate(startDate);
    }
    if (range) {
      setRangeMode(range);
    }
    if (display) {
      setDisplayMode(display);
    }

    // --- 3. Update local storage ---
    if (display) {
      localStorage.setItem(LocalStorageKey.CalendarDisplay, display);
    }
    if (range) {
      localStorage.setItem(LocalStorageKey.CalendarRange, range);
    }
  };

  return (
    <>
      <Stack direction="row">
        {/* ------------- SIDE PANEL ------------- */}
        <PlannerSiderPanel
          entityId={entityId}
          remindersAvailable={remindersAvailable}
          showReminders={false}
          date={endDate}
          onDateChange={(date: Moment) => {
            updateSearchOptions({
              startDate: calcStartDateByRange('1d', date),
              endDate: calcEndDateByRange('1d', date),
            });
          }}
          activitiesSectorsAndGroupsData={activitiesSectorsAndGroups}
          onFilterActivitiesGroupsChange={(filtersGroups) => {
            data.filterDataByActivitiesGroups(filtersGroups);
          }}
        />

        {/* ------------- MAIN CONTENT ------------- */}
        <Box>
          <Box
            sx={{
              position: 'absolute',
              width: '-webkit-fill-available',
              marginRight: '16px',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack flexDirection="column" flex="1" gap="14px" width="100%" minWidth="838px">
              {/* --- HEADER CONTENT --- */}
              <PlannerHeader
                startDate={startDate}
                endDate={endDate}
                display={displayMode}
                activitiesSectorsAndGroupsData={activitiesSectorsAndGroups}
                usersData={data.usersData}
                caregiversData={data.caregiversData}
                onDateChange={(startDate, endDate, range) => {
                  updateSearchOptions({startDate, endDate, range});
                }}
                onDisplayChange={(display) => {
                  updateSearchOptions({display});
                }}
                onAddSelection={(item) => {
                  setAddSelectedItem(item);
                }}
                onFilterUserChange={(user) => {
                  data.filterDataByUser(user);
                }}
                onFilterCaregiverChange={(caregiver) => {
                  data.filterDataByCaregiver(caregiver);
                }}
                onFiltersChange={(filters) => {
                  data.filterDataByUserFilters(filters);
                }}
              />

              {/* --- BODY CONTENT --- */}
              <PlannerBody
                data={data}
                date={endDate}
                display={displayMode}
                range={rangeMode}
                isLoading={data.fetching}
                onItemClicked={itemMenu.selectItem}
                itemSelected={itemMenu.selectedItem}
              />
            </Stack>
          </Box>
        </Box>
      </Stack>

      {/* ------------- ITEM MENU DROPDOWN ------------- */}
      {itemMenu.selectedItem && (
        <PlannerItemMenu
          open={itemMenu.open}
          anchorEl={itemMenu.anchorEl}
          item={itemMenu.selectedItem}
          onClose={itemMenu.clearSelectedItem}
          onDelete={itemMenu.prepareToDelete}
          onRegister={itemMenu.prepareToRegister}
        />
      )}

      {/* ------------- MODALS ------------- */}
      {/* --- Create Activity --- */}
      {addSelectedItem?.type === 'activities-sector' && (
        <PlannerCreateActivityModal
          open
          activitiesSector={addSelectedItem.data.sector}
          onClose={() => {
            setAddSelectedItem(undefined);
          }}
          onSave={(numActivitiesCreated) => {
            setAddSelectedItem(undefined);
            data.refresh();
            snackbar.setOfficeToastNotification({
              severity: 'success',
              message: t(translations.planner.modal.create.activity.success, {
                count: numActivitiesCreated,
              }),
            });
          }}
        />
      )}
      {/* --- Create Anniversary --- */}
      {addSelectedItem?.type === 'anniversary' && (
        <PlannerCreateAnniversaryModal
          open
          onClose={() => {
            setAddSelectedItem(undefined);
          }}
        />
      )}

      {/* --- Confirm Deletion --- */}
      {itemMenu.dataToDelete.length > 0 && (
        <PlannerConfirmItemDeleteModal
          open
          data={itemMenu.dataToDelete}
          onCancel={itemMenu.clearDelete}
          onConfirm={async () => {
            try {
              await itemMenu.deleteData();
              data.refresh();
            } catch (error) {
              console.error(error);
              snackbar.setOfficeToastNotification({
                severity: 'error',
                message: t(translations.planner.errors.delete_activity),
              });
            }
          }}
        />
      )}

      {/* --- Register Activity --- */}
      {itemMenu.dataToRegisterItem && (
        <PlannerRegisterActivityModal
          open
          data={itemMenu.dataToRegisterItem}
          canWrite={itemMenu.dataToRegisterItem.canWrite}
          onSave={() => {
            itemMenu.clearRegisterData();
            data.refresh();
          }}
          onClose={() => {
            itemMenu.clearRegisterData();
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------

function getInitialRangeMode(searchParams: PlannerURLParams): DateRangeType {
  // 1. Use url param if available
  if (searchParams.range) {
    return searchParams.range;
  }

  // 2. Use local storage if available
  const storedRange = localStorage.getItem(LocalStorageKey.CalendarRange) as DateRangeType | null;

  switch (storedRange) {
    case '1d':
    case '1w':
    case '1m':
    case '7d':
    case '30d':
    case '180d':
    case '365d':
      return storedRange;
    default:
      return '1d';
  }
}

function getInitialDisplayMode(searchParams: PlannerURLParams): PlannerDisplayMode {
  // 1. Use url param if available
  if (searchParams.display) {
    return searchParams.display;
  }

  // 2. Use local storage if available
  const storedDisplay = localStorage.getItem(
    LocalStorageKey.CalendarDisplay
  ) as PlannerDisplayMode | null;

  switch (storedDisplay) {
    case 'calendar':
    case 'agenda':
      return storedDisplay;
    default:
      return 'agenda';
  }
}
